/** @jsx jsx */
import React, { useCallback } from 'react';
import noop from 'lodash/noop';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Spinner from '@atlaskit/spinner';
import EmptyState from '@atlaskit/empty-state';
import { LinkButton } from '@atlaskit/button/new';
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { ModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import type {
	ApplicationLinkOAuth2ConfigInfo,
	ApplicationLinkV3Data,
	ApplicationLinkV3Status,
} from '../../common/entities.tsx';
import ProductCard from '../common/product-card/index.tsx';
import { lookupTypeByTypeId } from '../../common/application-types.tsx';
import { APPLINKS_LIST_PAGE_URL } from '../../common/constants.tsx';
import ApplinksOAuth2EditPageContents from './oauth2/index.tsx';
import ApplinksOAuth1EditPageContents from './oauth1/index.tsx';

const styles = cssMap({
	applinksPageContents: {
		maxWidth: '900px',
		margin: '0 auto',
		paddingTop: token('space.400'),
		paddingRight: token('space.400'),
		paddingBottom: token('space.400'),
		paddingLeft: token('space.400'),
	},
});

const DisplayError = (props: { header: string; description: string }) => {
	return (
		<Box xcss={styles.applinksPageContents}>
			<EmptyState
				header={props.header}
				description={props.description}
				primaryAction={
					<LinkButton href="/plugins/servlet/applinks/listApplicationLinks" appearance="primary">
						See all application links
					</LinkButton>
				}
			/>
		</Box>
	);
};

export type ApplinksNotEditableProps = {
	kind: 'NOT_EDITABLE';
	reason: string;
};

export type ApplinksOAuth1Props = {
	kind: 'OAUTH1';
	applink: ApplicationLinkV3Data;
	status: ApplicationLinkV3Status | null;
};

export type ApplinksOAuth2Props = {
	kind: 'OAUTH2';
	applink: ApplicationLinkV3Data;
	oauth2ConfigInfo: ApplicationLinkOAuth2ConfigInfo;
};

export type ApplinksProps = ApplinksNotEditableProps | ApplinksOAuth1Props | ApplinksOAuth2Props;

type ApplinksEditPageContentsProps = {
	isLoading?: boolean;
	data: ApplinksProps | null;
	status: ApplicationLinkV3Status | null;
};

const breadcrumbs = (
	<Breadcrumbs onExpand={noop}>
		<BreadcrumbsItem
			text="Application Links"
			key="Application Links"
			href={APPLINKS_LIST_PAGE_URL}
		/>
	</Breadcrumbs>
);

const ApplinksEditPageContents = (props: ApplinksEditPageContentsProps) => {
	const getStatus = useCallback(() => {
		if (props.status === null) {
			return 'loading';
		}
		if (props.status.working) {
			return 'connected';
		}
		if (props.status.error && props.status.error.category === 'PARTIALLY_CONNECTED') {
			return 'partially-connected';
		}
		return 'error';
	}, [props.status]);

	const renderContents = useCallback(() => {
		if (props.isLoading) {
			return (
				<Inline alignInline="center" space="space.100" xcss={styles.applinksPageContents}>
					<Spinner />
				</Inline>
			);
		}
		if (props.data === null) {
			return (
				<DisplayError
					header="Application link not found"
					description="The application link you are trying to edit does not exist or has been deleted."
				/>
			);
		}
		if (props.data.kind === 'NOT_EDITABLE') {
			return <DisplayError header="Not Editable" description={props.data.reason} />;
		}
		return (
			<Stack grow="fill" xcss={styles.applinksPageContents}>
				<PageHeader breadcrumbs={breadcrumbs}>Edit application link</PageHeader>
				<Stack space="space.200">
					<Text>
						Secured by{' '}
						<Text as="strong">{props.data.applink.data.isOAuth2 ? 'OAuth 2.0' : 'OAuth 1.0'}</Text>
					</Text>
					<ProductCard
						applicationType={lookupTypeByTypeId(props.data.applink.type, false)}
						displayUrl={props.data.applink.displayUrl}
						heading="Linked application"
						connectedStatus={getStatus()}
					/>
				</Stack>
				{props.data.kind === 'OAUTH2' ? (
					<ApplinksOAuth2EditPageContents
						applink={props.data.applink}
						oauth2ConfigInfo={props.data.oauth2ConfigInfo}
					/>
				) : (
					<ApplinksOAuth1EditPageContents applink={props.data.applink} status={props.status} />
				)}
			</Stack>
		);
	}, [getStatus, props.data, props.isLoading, props.status]);

	return (
		<>
			<JSErrorBoundary
				id="applinks-administration-edit-page"
				teamName="olympus"
				packageName="applinksAdministrationEditPage"
			>
				<ModalContextProvider>{renderContents()}</ModalContextProvider>
			</JSErrorBoundary>
		</>
	);
};

export default ApplinksEditPageContents;
